import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Calendar, CheckCircle, XCircle, Clock, Mail, MessageSquare, Phone, MapPin, Package, User, Filter, RefreshCw } from 'lucide-react';
import { 
  fetchReservations, 
  updateReservationStatus, 
  getReservationStats,
  subscribeToReservations,
  unsubscribeFromReservations,
  markEmailSent,
  markSmsSent
} from '../../services/reservationService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';


const AutomatedReservationConfirmationSystem = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sendingEmail, setSendingEmail] = useState(null);
  const [sendingSms, setSendingSms] = useState(null);

  // Load reservations and stats
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = filter !== 'all' ? { status: filter } : {};
      const [reservationsData, statsData] = await Promise.all([
        fetchReservations(filters),
        getReservationStats()
      ]);

      setReservations(reservationsData);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();

    // Subscribe to realtime changes
    const subscription = subscribeToReservations((payload) => {
      console.log('Realtime update:', payload);
      loadData(); // Reload data on any change
    });

    return () => {
      unsubscribeFromReservations(subscription);
    };
  }, [filter]);

  // Send confirmation email
  const handleSendEmail = async (reservation) => {
    if (!reservation?.client_email) {
      alert('Aucune adresse email disponible pour ce client');
      return;
    }

    try {
      setSendingEmail(reservation?.id);

      const response = await supabase?.functions?.invoke('send-reservation-confirmation', {
        body: {
          reservationId: reservation?.id,
          clientName: reservation?.client_name,
          clientEmail: reservation?.client_email,
          clientPhone: reservation?.client_phone,
          pickupAddress: reservation?.pickup_address,
          deliveryAddress: reservation?.delivery_address,
          requestedPickupDate: reservation?.requested_pickup_date
        }
      });

      if (response?.error) throw response?.error;

      // Mark email as sent
      await markEmailSent(reservation?.id);
      await loadData();

      alert('✅ Email de confirmation envoyé avec succès!');
    } catch (err) {
      console.error('Error sending email:', err);
      alert(`❌ Erreur lors de l'envoi de l'email: ${err?.message || 'Erreur inconnue'}`);
    } finally {
      setSendingEmail(null);
    }
  };

  // Send confirmation SMS (placeholder - requires SMS provider integration)
  const handleSendSms = async (reservation) => {
    if (!reservation?.client_phone) {
      alert('Aucun numéro de téléphone disponible pour ce client');
      return;
    }

    try {
      setSendingSms(reservation?.id);

      // TODO: Integrate with SMS provider (Twilio, etc.)
      // For now, just mark as sent
      await markSmsSent(reservation?.id);
      await loadData();

      alert('✅ SMS de confirmation envoyé avec succès!');
    } catch (err) {
      console.error('Error sending SMS:', err);
      alert(`❌ Erreur lors de l'envoi du SMS: ${err?.message || 'Erreur inconnue'}`);
    } finally {
      setSendingSms(null);
    }
  };

  // Update reservation status
  const handleStatusChange = async (reservationId, newStatus) => {
    try {
      await updateReservationStatus(reservationId, newStatus);
      await loadData();
    } catch (err) {
      console.error('Error updating status:', err);
      alert(`Erreur lors de la mise à jour: ${err?.message}`);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Non précisée';
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString)?.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      confirmed: { label: 'Confirmée', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      completed: { label: 'Complétée', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-800', icon: XCircle }
    };

    const badge = badges?.[status] || badges?.pending;
    const Icon = badge?.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge?.color}`}>
        <Icon className="w-4 h-4" />
        {badge?.label}
      </span>
    );
  };

  if (loading && !reservations?.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement des réservations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Bell className="w-8 h-8 text-green-600" />
                Réservations Fatou IA
              </h1>
              <p className="mt-2 text-gray-600">
                Gestion des réservations capturées par Fatou avec notifications automatiques
              </p>
            </div>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.total}</p>
                </div>
                <Package className="w-8 h-8 text-gray-400" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">En attente</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats?.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Confirmées</p>
                  <p className="text-2xl font-bold text-blue-600">{stats?.confirmed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Complétées</p>
                  <p className="text-2xl font-bold text-green-600">{stats?.completed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Aujourd'hui</p>
                  <p className="text-2xl font-bold text-purple-600">{stats?.today}</p>
                </div>
                <Calendar className="w-8 h-8 text-purple-400" />
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-gray-400" />
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 'all', label: 'Toutes' },
                { value: 'pending', label: 'En attente' },
                { value: 'confirmed', label: 'Confirmées' },
                { value: 'completed', label: 'Complétées' },
                { value: 'cancelled', label: 'Annulées' }
              ]?.map((filterOption) => (
                <button
                  key={filterOption?.value}
                  onClick={() => setFilter(filterOption?.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filter === filterOption?.value
                      ? 'bg-green-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filterOption?.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Reservations List */}
        {reservations?.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucune réservation</h3>
            <p className="text-gray-600">
              {filter === 'all' ?'Aucune réservation capturée par Fatou pour le moment.'
                : `Aucune réservation avec le statut "${filter}".`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {reservations?.map((reservation) => (
              <div key={reservation?.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <User className="w-5 h-5 text-gray-400" />
                        <h3 className="text-lg font-semibold text-gray-900">{reservation?.client_name}</h3>
                        {getStatusBadge(reservation?.status)}
                      </div>
                      <p className="text-sm text-gray-500">
                        Créée le {formatDateTime(reservation?.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* Client Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Téléphone</p>
                        <p className="text-sm text-gray-900">{reservation?.client_phone || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Email</p>
                        <p className="text-sm text-gray-900">{reservation?.client_email || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Addresses */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {reservation?.pickup_address && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">Collecte</p>
                          <p className="text-sm text-gray-900">{reservation?.pickup_address}</p>
                          {reservation?.pickup_city && (
                            <p className="text-xs text-gray-600">{reservation?.pickup_city} {reservation?.pickup_postal_code}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {reservation?.delivery_address && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-orange-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">Livraison</p>
                          <p className="text-sm text-gray-900">{reservation?.delivery_address}</p>
                          {reservation?.delivery_city && (
                            <p className="text-xs text-gray-600">
                              {reservation?.delivery_city}
                              {reservation?.delivery_quartier && ` - ${reservation?.delivery_quartier}`}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Pickup Date */}
                  {reservation?.requested_pickup_date && (
                    <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 rounded-lg">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Date de collecte souhaitée</p>
                        <p className="text-sm text-gray-900">{formatDate(reservation?.requested_pickup_date)}</p>
                      </div>
                    </div>
                  )}

                  {/* Item Description */}
                  {reservation?.item_description && (
                    <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                      <Package className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Article</p>
                        <p className="text-sm text-gray-900">{reservation?.item_description}</p>
                      </div>
                    </div>
                  )}

                  {/* Notification Status */}
                  <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-700">
                        Email: {reservation?.confirmation_email_sent ? (
                          <span className="text-green-600 font-medium">✓ Envoyé</span>
                        ) : (
                          <span className="text-gray-500">Non envoyé</span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-700">
                        SMS: {reservation?.confirmation_sms_sent ? (
                          <span className="text-green-600 font-medium">✓ Envoyé</span>
                        ) : (
                          <span className="text-gray-500">Non envoyé</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                    {/* Send Email */}
                    <button
                      onClick={() => handleSendEmail(reservation)}
                      disabled={!reservation?.client_email || sendingEmail === reservation?.id || reservation?.confirmation_email_sent}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    >
                      <Mail className="w-4 h-4" />
                      {sendingEmail === reservation?.id ? 'Envoi...' : reservation?.confirmation_email_sent ? 'Email envoyé' : 'Envoyer Email'}
                    </button>

                    {/* Send SMS */}
                    <button
                      onClick={() => handleSendSms(reservation)}
                      disabled={!reservation?.client_phone || sendingSms === reservation?.id || reservation?.confirmation_sms_sent}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    >
                      <MessageSquare className="w-4 h-4" />
                      {sendingSms === reservation?.id ? 'Envoi...' : reservation?.confirmation_sms_sent ? 'SMS envoyé' : 'Envoyer SMS'}
                    </button>

                    {/* Status Actions */}
                    {reservation?.status === 'pending' && (
                      <button
                        onClick={() => handleStatusChange(reservation?.id, 'confirmed')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Confirmer
                      </button>
                    )}

                    {reservation?.status === 'confirmed' && (
                      <button
                        onClick={() => handleStatusChange(reservation?.id, 'completed')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Marquer complétée
                      </button>
                    )}

                    {(reservation?.status === 'pending' || reservation?.status === 'confirmed') && (
                      <button
                        onClick={() => handleStatusChange(reservation?.id, 'cancelled')}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                      >
                        <XCircle className="w-4 h-4" />
                        Annuler
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomatedReservationConfirmationSystem;